import { Component, contentChild, input, output } from '@angular/core';
import { Button, Popover } from '@ntv360/component-pantry';
import { SVG_ICONS } from '@shared/constants';

import { ExternalFilterChip, SearchbarComponent, TableToolbarButton } from '@shared';
import { FilterDropdownComponent } from '@shared/components/filter-dropdown/filter-dropdown.component';

@Component({
    selector: 'app-table-toolbar',
    standalone: true,
    imports: [Button, SearchbarComponent, Popover],
    templateUrl: './table-toolbar.component.html',
    styleUrl: './table-toolbar.component.scss',
})

/**
 * This component will be used in the table to display the toolbar.
 * Searchbar, filter, Bulk actions and export button.
 * */
export class TableToolbarComponent {
    /**
     * Reference to navbar icon constants.
     */
    public icons: Record<string, string> = SVG_ICONS;

    /**
     * Reference to a projected FilterDropdownComponent, used to render the active-filter chips row.
     */
    protected filterDropdown = contentChild(FilterDropdownComponent);

    /**
     * Additional chips from outside components (e.g. timezone dropdown) to display in the chips row.
     */
    public additionalChips = input<ExternalFilterChip[]>([]);

    /**
     * Placeholder text for the search bar.
     */
    public placeholder = input<string>('Search Content Data');

    /**
     * List of dynamic buttons to display in the toolbar.
     */
    public buttons = input<TableToolbarButton[]>([]);

    /** Button icon placement. Either 'left' or 'right' */
    public buttonIconPlacement = input<'left' | 'right'>('right');

    /**
     * Debounce time for the search input. Default is `500ms`.
     */
    public debounceTime = input<number>(500);

    /**
     * Disables enter-key/triggered search event handling for specific pages.
     */
    public disableEnterSearch = input<boolean>(false);

    /**
     * Emitted when a dynamic button is clicked.
     * Returns the button name/action.
     */
    public actionClick = output<string>();

    /**
     * Emitted when the bulk delete button is clicked.
     */
    public bulkDelete = output<void>();

    /**
     * Emitted when the export button is clicked.
     */
    public export = output<void>();

    /**
     * Emitted when the reset button is clicked.
     */
    public reset = output<void>();

    /**
     * Emitted when the search value changes.
     */
    public searchValueChange = output<string>();

    /**
     * Emitted when the search button is clicked.
     */
    public searchButtonClick = output<void>();

    /**
     * Timeout id used for debouncing the value of search input
     */
    private searchTimeout: ReturnType<typeof setTimeout> | null = null;

    /**
     * Triggers when the search value changes. Debounces the value by `debounceTime` ms.
     * @param value - Search value
     */
    public onSearchValueChange(value: string): void {
        clearTimeout(this.searchTimeout!);
        this.searchTimeout = setTimeout(() => {
            this.searchValueChange.emit(value);
        }, this.debounceTime());
    }

    /**
     * @public
     * @description Handles enter/search-triggered callback from searchbar.
     * @returns void
     */
    public onSearchButtonClick(): void {
        if (this.disableEnterSearch()) {
            return;
        }

        this.searchButtonClick.emit();
    }

    /**
     * @protected
     * @description Clears all active filter chips from dropdown and external sources.
     * @returns void
     */
    protected onClearAllChips(): void {
        this.filterDropdown()?.onClearAll();
        this.additionalChips().forEach((chip) => chip.onRemove());
    }

    /**
     * Handles dynamic button clicks.
     * @param action - Action name
     */
    public onActionClick(action: string): void {
        this.actionClick.emit(action);

        // Backward compatibility for specific outputs
        if (action.toLowerCase().includes('delete')) {
            this.bulkDelete.emit();
        } else if (action.toLowerCase().includes('export')) {
            this.export.emit();
        } else if (action.toLowerCase().includes('reset')) {
            this.reset.emit();
        }
    }
}
